import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  CloudUpload as UploadIcon,
  CloudSync as ProcessingIcon,
} from '@mui/icons-material';
import { UploadItem } from '../../contexts/UploadContext';

interface UploadProgressBarProps {
  uploads: UploadItem[];
  onCancel: (uploadId: string) => void;
}

const OUTLOOK_BLUE = '#0078D4';

const UploadProgressBar: React.FC<UploadProgressBarProps> = ({ uploads, onCancel }) => {
  if (uploads.length === 0) return null;

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
      {uploads.map((upload) => (
        <Paper
          key={upload.id}
          elevation={0}
          sx={{
            p: 1,
            mb: uploads.indexOf(upload) < uploads.length - 1 ? 1 : 0,
            backgroundColor: 'white',
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Icon */}
            {upload.status === 'uploading' && (
              <UploadIcon sx={{ color: OUTLOOK_BLUE, fontSize: 20 }} />
            )}
            {upload.status === 'processing' && (
              <CircularProgress size={20} sx={{ color: OUTLOOK_BLUE }} />
            )}
            {upload.status === 'completed' && (
              <CheckIcon sx={{ color: '#4caf50', fontSize: 20 }} />
            )}
            {upload.status === 'error' && (
              <ErrorIcon sx={{ color: '#f44336', fontSize: 20 }} />
            )}

            {/* File info and progress */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '70%',
                  }}
                >
                  {upload.fileName}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {formatFileSize(upload.fileSize)}
                </Typography>
              </Box>

              {upload.status === 'uploading' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={upload.progress}
                    sx={{
                      flex: 1,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: OUTLOOK_BLUE,
                        borderRadius: 3,
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#666', minWidth: 35 }}>
                    {upload.progress}%
                  </Typography>
                </Box>
              )}

              {upload.status === 'processing' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <LinearProgress
                    variant="indeterminate"
                    sx={{
                      flex: 1,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: OUTLOOK_BLUE,
                        borderRadius: 3,
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: OUTLOOK_BLUE, minWidth: 80 }}>
                    Traitement...
                  </Typography>
                </Box>
              )}

              {upload.status === 'completed' && (
                <Typography variant="caption" sx={{ color: '#4caf50' }}>
                  Upload terminé
                </Typography>
              )}

              {upload.status === 'error' && (
                <Typography variant="caption" sx={{ color: '#f44336' }}>
                  {upload.error || 'Erreur lors de l\'upload'}
                </Typography>
              )}
            </Box>

            {/* Cancel button (only for uploading) */}
            {upload.status === 'uploading' && (
              <IconButton
                size="small"
                onClick={() => onCancel(upload.id)}
                sx={{ color: '#999' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default UploadProgressBar;
